import React, { SyntheticEvent, useCallback, useEffect, useMemo, useState } from "react";

import moment from "moment";

import ButtonLoadCheck from "react-lib/appcon/common/ButtonLoadCheck";

import ModConfirm from "react-lib/apps/common/cnmi/ModConfirm";
import DateTextBox from "react-lib/apps/common/DateTextBox";
import SnackMessage from "react-lib/apps/common/SnackMessage";
import TimeComboBox from "react-lib/apps/common/TimeComboBox";

import CardRehabDeviceUX from "./CardRehabDeviceUX";

import {
  ACTIONS,
  BTN_ACTS,
  MasterOptions,
  OnEvent,
  PickedState,
  RehabDeviceDetailType,
  RunSequence,
  SEQUENCE_NAME,
  SetProp,
  State,
} from "./sequence/RehabDevice";

// Types
type CardRehabDeviceProps = Pick<
  PickedState,
  "buttonLoadCheck" | "errorMessage" | "selectedDivision"
> & {
  onEvent: OnEvent;
  setProp: SetProp;
  runSequence: RunSequence;
  RehabDeviceSequence: State["RehabDeviceSequence"];
  languageUX: string;
  masterOptions: MasterOptions;
};

type FormErrorsType = {
  qty?: boolean;
  time?: boolean;
};

const CardRehabDevice = (props: CardRehabDeviceProps) => {
  const [openModDelete, setOpenModDelete] = useState<boolean>(false);
  const [modConfirmInactive, setModConfirmInactive] = useState<{ inactiveDate?: string } | null>(
    null
  );

  const [formErrors, setFormErrors] = useState<FormErrorsType | null>(null);

  const handleSearch = useCallback(() => {
    props.runSequence({ sequence: "RehabDevice", action: ACTIONS.SEARCH });
  }, []);

  // Effect
  useEffect(() => {
    setFormErrors(null);

    props.runSequence({ sequence: "RehabDevice", restart: true });

    return () => {
      props.setProp(`errorMessage.${SEQUENCE_NAME}`, null);
    };
  }, []);

  useEffect(() => {
    if (props.RehabDeviceSequence?.filterDevice) {
      props.setProp(
        "RehabDeviceSequence.filterDevice",
        { division: props.selectedDivision?.id },
        () => {
          handleSearch();
        }
      );
    }
  }, [props.selectedDivision?.id]);

  const deviceDetail = useMemo(
    () => props.RehabDeviceSequence?.deviceDetail,
    [props.RehabDeviceSequence?.deviceDetail]
  );

  const readOnly = useMemo(() => {
    // กรณี division ของ device ไม่ตรงกับ division ที่เลือก
    const isDifferentDivision =
      !!deviceDetail?.division && props.selectedDivision?.id !== deviceDetail.division;
    // กรณี device ที่ใช้ฟรี (device_free) แต่สถานะ inactive
    const isInactiveFreeDevice = deviceDetail?.device_free && !deviceDetail.isActive;

    return isDifferentDivision || isInactiveFreeDevice;
  }, [deviceDetail, props.selectedDivision]);

  const inactiveDate = useMemo(() => {
    if (!deviceDetail?.inactive_date || deviceDetail.active) {
      return "";
    }

    return moment(deviceDetail.inactive_date).format("MMMM DD, YYYY");
  }, [deviceDetail?.active, deviceDetail?.inactive_date]);

  const formattedDevice = useMemo(() => {
    const list = props.RehabDeviceSequence?.deviceList ?? [];

    return list.map((item) => {
      const division = props.masterOptions.division?.find(
        (option) => Number(option.value) === item.division
      );

      return {
        ...item,
        divisionName: division?.text,
        // 10:00:00 -> 10:00
        end_time: /\d{2}:\d{2}/.exec(item.end_time)?.[0] || "",
        start_time: /\d{2}:\d{2}/.exec(item.start_time)?.[0] || "",
      };
    });
  }, [props.masterOptions, props.RehabDeviceSequence?.deviceList]);

  const handleChangeValue = (e: SyntheticEvent | null, data: Record<string, any>) => {
    const name = data.name;
    const value = data.type === "checkbox" ? data.checked : data.value;

    if (!name || !deviceDetail) {
      return;
    }

    props.setProp("RehabDeviceSequence.deviceDetail", {
      ...deviceDetail,
      [name]: value,
    });
  };

  const handleChangeFilter = (e: SyntheticEvent | null, data: Record<string, any>) => {
    const name = data.name;
    const value = data.type === "checkbox" ? data.checked : data.value;

    if (!name) {
      return;
    }

    props.setProp("RehabDeviceSequence.filterDevice", {
      ...props.RehabDeviceSequence?.filterDevice,
      [name]: value,
    });
  };

  const handleChangeTime = (name: string) => (value: string) => {
    handleChangeValue(null, { name, value });
  };

  const handleSave = (isConfirm?: boolean) => {
    if (!deviceDetail) {
      return;
    }

    const errors: FormErrorsType = {};
    const time = Number(deviceDetail.time_per_slot) || 0;
    const qty = Number(deviceDetail.qty_of_device) || 0;

    // ตรวจสอบจำนวนอุปกรณ์ ต้องมากกว่า 0 ถ้าไม่ได้ตั้งให้ใช้ฟรี (device_free = false)
    if (!deviceDetail.device_free && qty <= 0) {
      errors.qty = true;
    }

    // ตรวจสอบเวลา ต้องไม่เกิน 60 และไม่น้อยกว่า 0 และหาร 60 ลงตัวพอดี
    if (time > 60 || time <= 0 || 60 % time !== 0) {
      errors.time = true;
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);

      return;
    }

    setFormErrors(null);

    const isDeviceAvailable = !!deviceDetail.device_free || !!deviceDetail.has_appointments;

    // ถ้าเป็น device_free และสถานะ inactive และยังไม่ได้ confirm -> เปิด modal ยืนยัน
    if (isDeviceAvailable && !deviceDetail.active && !isConfirm) {
      setModConfirmInactive({});

      return;
    }

    props.runSequence({
      sequence: "RehabDevice",
      action: ACTIONS.SAVE,
      inactiveDate: modConfirmInactive?.inactiveDate,
      onSuccess: handleCloseModConfirmInactive,
    });
  };

  const handleCloseErrMsg = () => {
    props.setProp(`errorMessage.${SEQUENCE_NAME}`, null);
  };

  const handleClear = () => {
    setFormErrors(null);

    props.runSequence({ sequence: "RehabDevice", action: ACTIONS.CLEAR });
  };

  const handleSelectDevice = (data: RehabDeviceDetailType) => () => {
    props.setProp("RehabDeviceSequence.deviceDetail", { ...data, isActive: data.active });
  };

  const handleOpenModDelete = () => {
    setOpenModDelete(true);
  };

  const handleCloseModDelete = () => {
    setOpenModDelete(false);
  };

  const handleCreateDeviceFree = () => {
    props.runSequence({ sequence: "RehabDevice", action: ACTIONS.CREATE_DEVICE_FREE });
  };

  const handleDelete = () => {
    props.runSequence({
      sequence: "RehabDevice",
      action: ACTIONS.DELETE,
      onSuccess: handleCloseModDelete,
    });
  };

  const handleCloseModConfirmInactive = () => {
    setModConfirmInactive(null);
  };

  console.log("CardRehabDevice props:", props);

  return (
    <div>
      <SnackMessage
        onEvent={props.onEvent}
        error={props.errorMessage?.[SEQUENCE_NAME]}
        success={null}
        onClose={handleCloseErrMsg}
      />

      <CardRehabDeviceUX
        deviceDetail={deviceDetail}
        filterDevice={props.RehabDeviceSequence?.filterDevice}
        formErrors={formErrors}
        inactiveDate={inactiveDate}
        readOnly={readOnly}
        showDeviceFree={props.RehabDeviceSequence?.showDeviceFree}
        languageUX={props.languageUX}
        onChangeFilter={handleChangeFilter}
        onChangeValue={handleChangeValue}
        onClear={handleClear}
        onCreateDeviceFree={handleCreateDeviceFree}
        onDelete={handleOpenModDelete}
        buttonSave={
          <ButtonLoadCheck
            setProp={props.setProp}
            color={deviceDetail?.id ? "yellow" : "green"}
            disabled={readOnly}
            paramKey={BTN_ACTS.SAVE}
            size="small"
            title={deviceDetail?.id ? "EDIT" : "CREATE"}
            buttonLoadCheck={props.buttonLoadCheck?.[BTN_ACTS.SAVE]}
            onClick={() => {
              handleSave();
            }}
          />
        }
        buttonSearch={
          <ButtonLoadCheck
            setProp={props.setProp}
            color="blue"
            paramKey={BTN_ACTS.SEARCH}
            size="small"
            title="SEARCH"
            buttonLoadCheck={props.buttonLoadCheck?.[BTN_ACTS.SEARCH]}
            onClick={handleSearch}
          />
        }
        deviceList={formattedDevice.map((item, index) => (
          <div
            key={`device-${item.id}`}
            style={{
              backgroundColor:
                item.id === deviceDetail?.id ? "var(--primary-select-background)" : "",
              borderBottom:
                formattedDevice.length - 1 === index ? "" : "1px solid rgba(196, 196, 196, 1)",
              cursor: "pointer",
              // margin: "0 -1rem",
              padding: "0.5rem 1rem",
            }}
            onClick={handleSelectDevice(item)}
          >
            <div>{item.name}</div>
            <div>Division : {item.divisionName}</div>
            <div>
              {item.qty_of_device ? `Quantity : ${item.qty_of_device}, ` : ""}Slot Duration:{" "}
              {item.time_per_slot} min, Hours: {item.start_time} - {item.end_time}
            </div>
            <div>Overbooking : {item.allow_overbooking ? "Allow" : "Not allow"}</div>
            <div>{item.active ? "Active" : "Inactive"}</div>
          </div>
        ))}
        endTime={
          <TimeComboBox
            defaultValue={deviceDetail?.end_time}
            disabled={deviceDetail?.has_appointments ?? readOnly}
            placeholder=""
            fluid
            onTextChange={handleChangeTime("end_time")}
          />
        }
        startTime={
          <TimeComboBox
            defaultValue={deviceDetail?.start_time}
            disabled={deviceDetail?.has_appointments ?? readOnly}
            placeholder=""
            fluid
            onTextChange={handleChangeTime("start_time")}
          />
        }
      />

      <ModConfirm
        basic={false}
        denyButtonStyle={{ width: "8rem" }}
        denyButtonText="CANCEL"
        openModal={openModDelete}
        titleColor="red"
        onCloseWithDimmerClick={handleCloseModDelete}
        onDeny={handleCloseModDelete}
        approveButton={
          <ButtonLoadCheck
            setProp={props.setProp}
            style={{ width: "8rem" }}
            color="green"
            paramKey={BTN_ACTS.DELETE}
            size="medium"
            title="OK, Delete"
            buttonLoadCheck={props.buttonLoadCheck?.[BTN_ACTS.DELETE]}
            onClick={handleDelete}
          />
        }
        content={
          <div style={{ fontWeight: "bold", marginBottom: "-0.5rem", textAlign: "center" }}>
            Would you like to delete this device?
          </div>
        }
      />

      <ModConfirm
        basic={false}
        denyButtonStyle={{ marginLeft: "-1rem", width: "9rem" }}
        denyButtonText="CANCEL"
        openModal={!!modConfirmInactive}
        titleColor="red"
        onCloseWithDimmerClick={handleCloseModConfirmInactive}
        onDeny={handleCloseModConfirmInactive}
        approveButton={
          <ButtonLoadCheck
            setProp={props.setProp}
            style={{ width: "9rem" }}
            color="green"
            disabled={deviceDetail?.has_appointments ? !modConfirmInactive?.inactiveDate : false}
            paramKey={BTN_ACTS.SAVE}
            size="medium"
            title="OK, Inactive"
            buttonLoadCheck={props.buttonLoadCheck?.[BTN_ACTS.SAVE]}
            onClick={() => {
              handleSave(true);
            }}
          />
        }
        content={
          <div style={{ marginBottom: "-0.5rem", textAlign: "center" }}>
            {deviceDetail?.has_appointments ? (
              <>
                <div style={{ color: "#ff1313", fontWeight: "bold", marginBottom: "1rem" }}>
                  Please specify the date it will become inactive.*
                </div>
                <DateTextBox
                  value={modConfirmInactive?.inactiveDate}
                  onChange={(value) => {
                    setModConfirmInactive({ ...modConfirmInactive, inactiveDate: value });
                  }}
                />
              </>
            ) : (
              <>
                <div>Do you want to inactive?</div>
                <div>Once inactive, it cannot be reactivated</div>
                <div>and a new one must be created instead.</div>
              </>
            )}
          </div>
        }
      />
    </div>
  );
};

CardRehabDevice.displayName = "CardRehabDevice";

export default React.memo(CardRehabDevice);
